/**
 * Read-only display layer for role permissions: AccessPolicy resource rules + ui-permissions extension.
 *
 * Role → AccessPolicy mapping uses `accessPolicyKeyForRole` (stable tag codes from seed).
 * UI permissions are read from the ui-permissions extension on a FHIR profile resource —
 * not from `defaultRolePermissions` (that object is runtime fallback only).
 */

import type { AccessPolicy, Extension } from '@medplum/fhirtypes';
import {
  POLICY_IDS,
  POLICY_TAG_CODES,
  POLICY_TAG_SYSTEM,
} from '@unify/fhir-model/structural-ids';
import type { Role } from '../../pages/users/types/rbac';
import { ROLE_LABELS, STAFF_ROLES } from '../../pages/users/types/rbac';
import { permissionCategories } from '../../pages/users/types/permissions';
import { accessPolicyKeyForRole } from './accessPolicy';
import { EXTENSION_URLS } from './constants';

export interface ResourcePermissionRow {
  /** Stable React key — AccessPolicy may repeat resourceType + criteria. */
  rowKey: string;
  resourceType: string;
  read: boolean;
  search: boolean;
  create: boolean;
  update: boolean;
  delete: boolean;
  scope?: string;
  conditionSummary?: string;
  warning?: string;
}

export interface UiPermissionRow {
  category: string;
  permission: string;
  permissionId: string;
  enabled: boolean;
  source: string;
}

export interface RolePermissionsViewModel {
  role: Role;
  roleLabel: string;
  accessPolicyKey?: string;
  accessPolicyId?: string;
  accessPolicyName?: string;
  resourceRows: ResourcePermissionRow[];
  uiRows: UiPermissionRow[];
  warnings: string[];
}

/** Whether the signed-in user may view AccessPolicy + UI permission reference tables. */
export function canViewRolePermissionsReference(viewerRole: Role): boolean {
  return STAFF_ROLES.includes(viewerRole);
}

/** Canonical app roles shown in the Settings permissions reference tabs. */
export const SETTINGS_REFERENCE_ROLES: Role[] = [
  'unify-admin',
  'unify-assistant',
  'practice-owner',
  'practice-admin',
  'practice-assistant',
  'supervisor',
  'provider',
  'client',
];

type FhirExtensionSource = {
  extension?: Extension[];
  resourceType?: string;
  id?: string;
};

/**
 * Resolve the AccessPolicy for a Unify role from a fetched policy list.
 * Matches canonical `meta.tag` first, then legacy fixed UUID (local docker).
 */
export function getAccessPolicyForRole(
  role: Role,
  policies: AccessPolicy[],
): AccessPolicy | undefined {
  const key = accessPolicyKeyForRole(role, role === 'client');
  const tagCode = POLICY_TAG_CODES[key];
  const byTag = policies.find((policy) =>
    policy.meta?.tag?.some(
      (tag) => tag.system === POLICY_TAG_SYSTEM && tag.code === tagCode,
    ),
  );
  if (byTag) return byTag;
  return policies.find((policy) => policy.id === POLICY_IDS[key]);
}

/** Convert AccessPolicy FHIR rules into readable table rows. */
export function normalizeAccessPolicyPermissions(accessPolicy: AccessPolicy): {
  rows: ResourcePermissionRow[];
  warnings: string[];
} {
  const warnings: string[] = [];
  const rows: Omit<ResourcePermissionRow, 'rowKey'>[] = [];

  if (accessPolicy.compartment?.reference) {
    warnings.push(`Policy-level compartment: ${accessPolicy.compartment.reference}`);
  }

  const resources = accessPolicy.resource ?? [];
  if (resources.length === 0) {
    warnings.push('AccessPolicy has no resource rules defined.');
  }

  for (const entry of resources) {
    rows.push(normalizeResourceEntry(entry as AccessPolicyResourceEntry, warnings));
  }

  rows.sort((a, b) => {
    const byType = a.resourceType.localeCompare(b.resourceType);
    if (byType !== 0) return byType;
    return (a.conditionSummary ?? '').localeCompare(b.conditionSummary ?? '');
  });

  return {
    rows: rows.map((row, index) => ({
      ...row,
      rowKey: resourcePermissionRowKey(row, index),
    })),
    warnings,
  };
}

function resourcePermissionRowKey(row: Omit<ResourcePermissionRow, 'rowKey'>, index: number): string {
  const flags = [row.read, row.search, row.create, row.update, row.delete]
    .map((enabled) => (enabled ? '1' : '0'))
    .join('');
  return `${row.resourceType}-${index}-${flags}-${row.conditionSummary ?? ''}-${row.scope ?? ''}`;
}

type AccessPolicyResourceEntry = NonNullable<AccessPolicy['resource']>[number] & {
  wildcard?: boolean;
  parameter?: Array<{ name?: string; valueReference?: string; valueString?: string }>;
};

const FULL_ACCESS_INTERACTIONS = ['create', 'read', 'search', 'update', 'delete'] as const;
const READ_ONLY_INTERACTIONS = ['read', 'search'] as const;

/** Medplum: omitted interaction = full CRUD; readonly (legacy) = read/search only. */
function resolveEntryInteractions(entry: AccessPolicyResourceEntry): Set<string> {
  if (entry.interaction?.length) {
    return new Set(entry.interaction);
  }
  if (entry.readonly) {
    return new Set(READ_ONLY_INTERACTIONS);
  }
  return new Set(FULL_ACCESS_INTERACTIONS);
}

function normalizeResourceEntry(
  entry: AccessPolicyResourceEntry,
  warnings: string[],
): Omit<ResourcePermissionRow, 'rowKey'> {
  const interactions = resolveEntryInteractions(entry);
  const resourceType = entry.resourceType ?? 'Unknown';
  const unrestricted =
    !entry.criteria && !entry.compartment?.reference && !entry.parameter?.length;

  if (!entry.resourceType) {
    warnings.push('Encountered a resource rule without resourceType.');
  }

  if (entry.wildcard) {
    warnings.push(`${resourceType}: wildcard rules are summarized only.`);
  }

  const conditionParts: string[] = [];
  if (entry.criteria) conditionParts.push(`criteria: ${entry.criteria}`);
  if (entry.compartment?.reference) {
    conditionParts.push(`compartment: ${entry.compartment.reference}`);
  }
  if (entry.parameter?.length) {
    const params = entry.parameter
      .map((param) => `${param.name}=${param.valueReference ?? param.valueString ?? ''}`)
      .join(', ');
    conditionParts.push(`parameters: ${params}`);
    warnings.push(`${resourceType}: parameterized rules may not display fully.`);
  }

  const fullAccessWithoutCriteria =
    unrestricted && !entry.readonly && !entry.interaction?.length;

  return {
    resourceType,
    read: interactions.has('read'),
    search: interactions.has('search'),
    create: interactions.has('create'),
    update: interactions.has('update'),
    delete: interactions.has('delete'),
    scope: entry.compartment?.reference ?? (fullAccessWithoutCriteria ? 'Full access' : undefined),
    conditionSummary: conditionParts.length > 0 ? conditionParts.join(' · ') : undefined,
    warning: entry.wildcard ? 'Wildcard rule' : undefined,
  };
}

/** Parse ui-permissions JSON from FHIR extensions (AccessPolicy or profile). */
export function extractUiPermissionsFromExtensions(
  resource?: FhirExtensionSource,
): { value: Record<string, unknown> | null; source: string; warning?: string } {
  if (!resource?.extension?.length) {
    return {
      value: null,
      source: 'none',
      warning: 'No ui-permissions extension found on the source resource.',
    };
  }

  const ext = resource.extension.find((entry) => entry.url === EXTENSION_URLS.uiPermissions);
  if (!ext?.valueString) {
    return {
      value: null,
      source: 'none',
      warning: 'No ui-permissions extension found on the source resource.',
    };
  }

  try {
    const parsed = JSON.parse(ext.valueString) as Record<string, unknown>;
    const sourceLabel =
      resource.resourceType && resource.id
        ? `${resource.resourceType}/${resource.id}`
        : 'extension';
    return { value: parsed, source: `${sourceLabel} extension` };
  } catch {
    return {
      value: null,
      source: 'extension',
      warning: 'ui-permissions extension contains invalid JSON.',
    };
  }
}

/** Flatten ui-permissions JSON into display rows (uses permissionCategories for labels). */
export function flattenUiPermissions(
  value: Record<string, unknown> | null,
  source: string,
): UiPermissionRow[] {
  if (!value) return [];

  const labelById = new Map<string, { label: string; category: string }>();
  for (const category of permissionCategories) {
    for (const permission of category.permissions) {
      labelById.set(permission.id, {
        label: permission.label,
        category: category.label,
      });
    }
  }

  const rows: UiPermissionRow[] = [];
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw !== 'boolean') continue;
    const meta = labelById.get(key);
    const enabled = key.startsWith('cannot_') ? raw === false : raw === true;
    rows.push({
      category: meta?.category ?? 'Other',
      permission: meta?.label ?? key,
      permissionId: key,
      enabled,
      source,
    });
  }

  rows.sort((a, b) => {
    const byCategory = a.category.localeCompare(b.category);
    if (byCategory !== 0) return byCategory;
    return a.permission.localeCompare(b.permission);
  });

  return rows;
}

export function buildRolePermissionsViewModel(
  role: Role,
  policies: AccessPolicy[],
  uiSourceResource?: FhirExtensionSource,
): RolePermissionsViewModel {
  const policyKey = accessPolicyKeyForRole(role, role === 'client');
  const mappedPolicyId = POLICY_IDS[policyKey];
  const policy = getAccessPolicyForRole(role, policies);
  const warnings: string[] = [];

  let resourceRows: ResourcePermissionRow[] = [];
  if (policy) {
    const normalized = normalizeAccessPolicyPermissions(policy);
    resourceRows = normalized.rows;
    warnings.push(...normalized.warnings);
  } else if (mappedPolicyId) {
    warnings.push(
      `Using canonical role mapping (${policyKey}), but AccessPolicy details are unavailable in this session.`,
    );
  } else {
    warnings.push(`No AccessPolicy mapping found for role "${role}".`);
  }

  const uiExtract = extractUiPermissionsFromExtensions(uiSourceResource);
  if (uiExtract.warning) warnings.push(uiExtract.warning);
  const uiRows = flattenUiPermissions(uiExtract.value, uiExtract.source);

  return {
    role,
    roleLabel: ROLE_LABELS[role],
    accessPolicyKey: policyKey,
    accessPolicyId: policy?.id ?? mappedPolicyId,
    accessPolicyName: policy?.name,
    resourceRows,
    uiRows,
    warnings: [...new Set(warnings)],
  };
}
